/**
 * PartyFormSlideOver — full Party form rendered as a slide-over per
 * the design folder's `openDetails('party-form')` pattern.
 *
 * Field set matches mobile's add-party.tsx 1:1:
 *   Identity:    Type · Name* · Phone* · Email · Joined
 *   Address:     Street · City · State · PIN  → concatenated to `address`
 *   Compliance:  Aadhar · PAN
 *   Opening Bal: Amount + Direction (to_pay / to_receive)
 *   Bank:        Holder · Bank · Acc · IFSC · Branch · UPI · IBAN
 *
 * Submit:
 *   - Validates name + Indian-E.164 phone
 *   - Guards against duplicate phone in the org
 *   - On success → calls `onSaved(partyId)`
 */
import { useEffect, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import {
  createParty,
  DuplicatePhoneError,
  formatIndianPhone,
  InvalidPhoneError,
  PARTY_TYPE_GROUPS,
  updateParty,
  type BankDetails,
  type Party,
  type PartyType,
} from '@/lib/parties';

const STYLE_INPUT: React.CSSProperties = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const STYLE_LABEL: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  display: 'block',
  color: 'var(--text-secondary)',
};

const STYLE_REQUIRED: React.CSSProperties = { color: 'var(--critical)' };

const STYLE_SECTION_TITLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 12,
  marginTop: 24,
  paddingBottom: 8,
  borderBottom: '1px solid var(--divider)',
};

function isoDate(d: Date | null | undefined): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export type PartyFormSlideOverProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  /** Pass to edit; omit for create. */
  party?: Party | null;
  /** Pre-select a party type on create. */
  defaultType?: PartyType;
  /** Pre-fill the name input on create. Used when the picker opens
   *  this form with a typed name from the search box. */
  defaultName?: string;
  onSaved?: (partyId: string) => void;
};

export function PartyFormSlideOver({
  open,
  onClose,
  orgId,
  party,
  defaultType = 'client',
  defaultName,
  onSaved,
}: PartyFormSlideOverProps) {
  const { user } = useAuth();
  const isEdit = Boolean(party);

  // Identity
  const [partyType, setPartyType] = useState<PartyType>(defaultType);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [joinedDate, setJoinedDate] = useState<string>('');

  // Address (split into 4 fields per the folder; concatenated on save)
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pin, setPin] = useState('');

  // KYC
  const [aadhar, setAadhar] = useState('');
  const [pan, setPan] = useState('');

  // Opening balance
  const [openingAmount, setOpeningAmount] = useState('');
  const [openingDir, setOpeningDir] = useState<'to_pay' | 'to_receive'>('to_pay');

  // Bank
  const [bank, setBank] = useState<BankDetails>({});

  // UI
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed for create + edit on open.
  useEffect(() => {
    if (!open) return;
    if (party) {
      setPartyType(party.partyType);
      setName(party.name ?? '');
      setPhone(party.phone ?? '');
      setEmail(party.email ?? '');
      setJoinedDate(
        party.dateOfJoining ? isoDate(new Date(party.dateOfJoining.toMillis())) : '',
      );
      // Address is stored concatenated; we leave the split fields blank
      // and offer a single street input that takes the whole string.
      setStreet(party.address ?? '');
      setCity('');
      setState('');
      setPin('');
      setAadhar(party.aadharNumber ?? '');
      setPan(party.panNumber ?? '');
      setOpeningAmount(
        typeof party.openingBalance === 'number' && party.openingBalance > 0
          ? String(party.openingBalance)
          : '',
      );
      setOpeningDir(party.openingBalanceType ?? 'to_pay');
      setBank(party.bankDetails ?? {});
    } else {
      setPartyType(defaultType);
      setName(defaultName ?? '');
      setPhone('');
      setEmail('');
      setJoinedDate('');
      setStreet('');
      setCity('');
      setState('');
      setPin('');
      setAadhar('');
      setPan('');
      setOpeningAmount('');
      setOpeningDir('to_pay');
      setBank({});
    }
    setError(null);
  }, [open, party, defaultType, defaultName]);

  // Esc + body scroll lock.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const handleSubmit = async () => {
    setError(null);
    if (!orgId) return setError('No organisation in scope.');
    if (!user) return setError('You must be signed in.');
    if (name.trim().length < 2) return setError('Name must be at least 2 characters.');

    const address = [street, city, state, pin].map((p) => p.trim()).filter(Boolean).join(', ');
    const openingBalance = openingAmount ? Number(openingAmount.replace(/,/g, '')) : 0;
    if (openingAmount && Number.isNaN(openingBalance)) {
      return setError('Opening balance must be a number.');
    }

    setBusy(true);
    try {
      let id: string;
      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        partyType,
        email: email.trim() || undefined,
        dateOfJoining: joinedDate ? new Date(`${joinedDate}T00:00:00`) : null,
        address: address || undefined,
        aadharNumber: aadhar.trim() || undefined,
        panNumber: pan.trim() || undefined,
        openingBalance,
        openingBalanceType: openingDir,
        bankDetails: bank,
      };
      if (isEdit && party) {
        await updateParty(party.id, payload);
        id = party.id;
      } else {
        id = await createParty({ ...payload, orgId, createdBy: user.uid });
      }
      onSaved?.(id);
      onClose();
    } catch (e) {
      if (e instanceof DuplicatePhoneError) {
        setError(`A party with this phone already exists: ${e.existing.name}.`);
      } else if (e instanceof InvalidPhoneError) {
        setError(e.message);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to save party.');
      }
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={handleClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label={isEdit ? 'Edit party' : 'New party'}
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">{isEdit ? 'Edit Party' : 'New Party'}</h2>
          <button
            type="button"
            className="slide-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            {/* Identity */}
            <div>
              <label style={STYLE_LABEL}>
                Party type <span style={STYLE_REQUIRED}>*</span>
              </label>
              <select
                style={STYLE_INPUT}
                value={partyType}
                onChange={(e) => setPartyType(e.currentTarget.value as PartyType)}
                disabled={busy}
              >
                {PARTY_TYPE_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.types.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div>
              <label style={STYLE_LABEL}>
                Name <span style={STYLE_REQUIRED}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Ravi Kumar / Balaji Steels"
                style={STYLE_INPUT}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                disabled={busy}
                required
                maxLength={100}
              />
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>
                  Phone <span style={STYLE_REQUIRED}>*</span>
                </label>
                <input
                  type="tel"
                  placeholder="10-digit mobile"
                  style={STYLE_INPUT}
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                  disabled={busy || isEdit /* phone changes are touchy; locked in edit */}
                  required
                />
                {isEdit ? (
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                    Phone locked on edit — change via mobile or delete + recreate.
                  </div>
                ) : null}
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Email</label>
                <input
                  type="email"
                  placeholder="optional"
                  style={STYLE_INPUT}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
            </div>

            <div>
              <label style={STYLE_LABEL}>Joined date</label>
              <input
                type="date"
                style={STYLE_INPUT}
                value={joinedDate}
                onChange={(e) => setJoinedDate(e.currentTarget.value)}
                disabled={busy}
              />
            </div>

            {/* Address */}
            <h3 style={STYLE_SECTION_TITLE}>Address</h3>
            <div>
              <label style={STYLE_LABEL}>Street / Building</label>
              <input
                type="text"
                placeholder="House, street, area"
                style={STYLE_INPUT}
                value={street}
                onChange={(e) => setStreet(e.currentTarget.value)}
                disabled={busy}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={STYLE_LABEL}>City</label>
                <input
                  type="text"
                  style={STYLE_INPUT}
                  value={city}
                  onChange={(e) => setCity(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
              <div>
                <label style={STYLE_LABEL}>State</label>
                <input
                  type="text"
                  style={STYLE_INPUT}
                  value={state}
                  onChange={(e) => setState(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
              <div>
                <label style={STYLE_LABEL}>PIN</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  style={STYLE_INPUT}
                  value={pin}
                  onChange={(e) => setPin(e.currentTarget.value.replace(/\D/g, ''))}
                  disabled={busy}
                />
              </div>
            </div>

            {/* Compliance */}
            <h3 style={STYLE_SECTION_TITLE}>Compliance</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Aadhar number</label>
                <input
                  type="text"
                  placeholder="XXXX XXXX XXXX"
                  maxLength={14}
                  style={STYLE_INPUT}
                  value={aadhar}
                  onChange={(e) => setAadhar(e.currentTarget.value)}
                  disabled={busy}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>PAN</label>
                <input
                  type="text"
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  style={{ ...STYLE_INPUT, textTransform: 'uppercase' }}
                  value={pan}
                  onChange={(e) => setPan(e.currentTarget.value.toUpperCase())}
                  disabled={busy}
                />
              </div>
            </div>

            {/* Opening balance */}
            <h3 style={STYLE_SECTION_TITLE}>Opening balance</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Amount (₹)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  style={STYLE_INPUT}
                  value={openingAmount}
                  onChange={(e) =>
                    setOpeningAmount(e.currentTarget.value.replace(/[^\d.]/g, ''))
                  }
                  disabled={busy}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Direction</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setOpeningDir('to_pay')}
                    style={{
                      flex: 1,
                      height: 40,
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                      background: openingDir === 'to_pay' ? 'var(--critical-bg)' : '#fff',
                      color: openingDir === 'to_pay' ? 'var(--critical)' : 'var(--text-secondary)',
                      fontSize: 12,
                      fontWeight: openingDir === 'to_pay' ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    You owe them
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpeningDir('to_receive')}
                    style={{
                      flex: 1,
                      height: 40,
                      borderRadius: 6,
                      border: '1px solid var(--border)',
                      background: openingDir === 'to_receive' ? '#F0FDF4' : '#fff',
                      color: openingDir === 'to_receive' ? 'var(--success)' : 'var(--text-secondary)',
                      fontSize: 12,
                      fontWeight: openingDir === 'to_receive' ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    They owe you
                  </button>
                </div>
              </div>
            </div>

            {/* Bank */}
            <h3 style={STYLE_SECTION_TITLE}>Bank details</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Account holder</label>
                <input
                  type="text"
                  style={STYLE_INPUT}
                  value={bank.accountHolderName ?? ''}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, accountHolderName: e.currentTarget.value }))
                  }
                  disabled={busy}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Bank name</label>
                <input
                  type="text"
                  style={STYLE_INPUT}
                  value={bank.bankName ?? ''}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, bankName: e.currentTarget.value }))
                  }
                  disabled={busy}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Account number</label>
                <input
                  type="text"
                  style={STYLE_INPUT}
                  value={bank.accountNumber ?? ''}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, accountNumber: e.currentTarget.value }))
                  }
                  disabled={busy}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>IFSC</label>
                <input
                  type="text"
                  style={{ ...STYLE_INPUT, textTransform: 'uppercase' }}
                  value={bank.ifsc ?? ''}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, ifsc: e.currentTarget.value.toUpperCase() }))
                  }
                  disabled={busy}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>UPI ID</label>
                <input
                  type="text"
                  placeholder="name@bank"
                  style={STYLE_INPUT}
                  value={bank.upiId ?? ''}
                  onChange={(e) => setBank((b) => ({ ...b, upiId: e.currentTarget.value }))}
                  disabled={busy}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={STYLE_LABEL}>Branch / Address</label>
                <input
                  type="text"
                  style={STYLE_INPUT}
                  value={bank.bankAddress ?? ''}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, bankAddress: e.currentTarget.value }))
                  }
                  disabled={busy}
                />
              </div>
            </div>

            {/* Footer actions */}
            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 12,
                paddingTop: 16,
                borderTop: '1px solid var(--border)',
              }}
            >
              <button
                type="button"
                className="btn-secondary"
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={handleClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={busy || !orgId}
              >
                {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Save party'}
              </button>
            </div>

            {!isEdit && phone.trim() ? (
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  marginTop: -8,
                  textAlign: 'right',
                }}
              >
                Will be saved as {formatIndianPhone(phone)}
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </>
  );
}
